import { trackEvent } from "@/lib/analytics";
import { useState } from "react";
import SEO from "@/components/SEO";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, TrendingUp, Briefcase, ShieldCheck, CheckCircle2, ArrowRight, Lock } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";

const schema = z.object({
  full_name: z.string().trim().min(2).max(100),
  fund: z.string().trim().min(2).max(120),
  objective: z.string().min(1),
  email: z.string().trim().email().max(255),
});

const PILLARS = [
  {
    icon: Search,
    title: "Operational Due Diligence (ODD)",
    desc: "Análisis profundo de la operativa retail antes de cerrar la inversión. Detectamos riesgos ocultos, validamos sinergias y cuantificamos potencial real de EBITDA.",
    n: "01",
  },
  {
    icon: TrendingUp,
    title: "Turnaround Management",
    desc: "Intervención ejecutiva en activos en dificultad. Reestructuración operativa, plan de 100 días y métricas semanales para revertir la tendencia y proteger el valor.",
    n: "02",
  },
  {
    icon: Briefcase,
    title: "Interim Operations",
    desc: "Liderazgo operativo temporal en tu participada. Cubrimos vacantes críticas (COO, Retail Director) con seniors que ejecutan tu tesis de inversión.",
    n: "03",
  },
];

export default function FamilyOfficesInversores() {
  const { lang } = useLanguage();
  useLocalizedRoute();
  const [form, setForm] = useState({ full_name: "", fund: "", objective: "", email: "" });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      const errs: Record<string, boolean> = {};
      result.error.issues.forEach((i) => { errs[i.path[0] as string] = true; });
      setErrors(errs);
      return;
    }
    setErrors({});
    setSending(true);

    const message = `[Family Office / Inversor]\nFondo / Family Office: ${form.fund}\nObjetivo: ${form.objective}\n\nSolicita reunión confidencial.`;

    await supabase.from("contact_leads" as any).insert({
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      profile_type: "investor",
      message,
      status: "new",
      source: "family_office_landing",
    });
    trackEvent("generate_lead", { lead_type: "family_office" });

    setSending(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-[#0A0E13] text-white">
      <SEO
        title={lang === "es"
          ? "Consultoría Retail para Inversores | Retail Lemon"
          : "Retail Consulting for Investors | Retail Lemon"}
        description={lang === "es"
          ? "Operational Due Diligence, Turnaround Management e Interim Operations para fondos y family offices con activos en retail. Validamos sinergias y cuantificamos potencial real de EBITDA."
          : "Operational Due Diligence, Turnaround Management, and Interim Operations for funds and family offices with retail assets. We validate synergies and quantify real EBITDA potential."}
        keywords={lang === "es"
          ? "due diligence retail, turnaround retail, interim management retail, family office retail, inversores retail, ebitda retail"
          : "retail due diligence, retail turnaround, retail interim management, retail family office, retail investors, retail ebitda"}
        path={lang === "en" ? "/en/family-offices-investors" : "/family-offices-inversores"}
        pathEs="/family-offices-inversores"
        pathEn="/en/family-offices-investors"
        lang={lang as "es" | "en"}
        type="website"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Retail Operational Due Diligence & Turnaround",
            name: lang === "es"
              ? "Consultoría Retail para Family Offices e Inversores"
              : "Retail Consulting for Family Offices and Investors",
            provider: {
              "@type": "Organization",
              name: "Retail Lemon",
              url: "https://retaillemon.com",
            },
            areaServed: "Worldwide",
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Family offices, private equity, investors with retail assets",
            },
            description: lang === "es"
              ? "Operational Due Diligence, Turnaround Management e Interim Operations para fondos y family offices con activos retail."
              : "Operational Due Diligence, Turnaround Management, and Interim Operations for funds and family offices with retail assets.",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Retail Lemon", item: "https://retaillemon.com/" },
              { "@type": "ListItem", position: 2, name: lang === "es" ? "Family Offices e Inversores" : "Family Offices & Investors", item: "https://retaillemon.com/family-offices-inversores" },
            ],
          },
        ]}
      />

      {/* Top utility bar */}
      <div className="border-b border-white/5">
        <section className="container max-w-6xl py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tight">
            RETAIL<span className="text-foreground">LEMON</span>
          </Link>
          <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">
            Private · Confidential · Investor Relations
          </span>
        </section>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E13]/70 via-[#0A0E13]/90 to-[#0A0E13]" aria-hidden />

        <section className="relative container max-w-5xl py-28 md:py-36 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 text-[11px] uppercase tracking-[0.25em] text-white/70 mb-8">
            <Briefcase className="w-3.5 h-3.5" /> Private Equity · Family Offices · M&A
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
            Maximizamos el valor y el <span className="text-foreground">EBITDA</span> <br className="hidden md:block" />
            de tus activos en Retail.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed font-light">
            Due Diligence operativa y gestión de Turnaround
            para <span className="text-white font-medium">proteger y multiplicar</span> tu inversión.
          </p>
          <a href="#contacto" className="inline-flex items-center gap-2 mt-12 text-sm uppercase tracking-[0.2em] text-white/80 hover:text-foreground transition-colors">
            Agendar reunión confidencial <ArrowRight className="w-4 h-4" />
          </a>
        </section>
      </section>

      {/* Pillars */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <section className="container max-w-6xl">
          <div className="text-center mb-20">
            <p className="text-[11px] uppercase tracking-[0.3em] text-foreground mb-4">Capacidades</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Tres servicios pensados para el ciclo de la inversión.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {PILLARS.map((p) => (
              <div key={p.n} className="bg-[#0A0E13] p-10 md:p-12 hover:bg-[#0F151D] transition-colors">
                <div className="flex items-center justify-between mb-8">
                  <p.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  <span className="font-display text-xs text-white/30 tracking-widest">{p.n}</span>
                </div>
                <h3 className="font-display text-xl font-semibold mb-4 leading-snug">
                  {p.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed font-light">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </section>
      </section>

      {/* Visual band */}
      <section className="relative h-[280px] md:h-[360px] overflow-hidden border-y border-white/5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center 35%",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/60 to-transparent" aria-hidden />
        <section className="relative container max-w-6xl h-full flex items-center">
          <p className="font-display text-2xl md:text-3xl max-w-2xl leading-snug text-white/90">
            “En retail, el <span className="text-foreground">deal</span> se gana en la operativa, no en el cierre.”
          </p>
        </section>
      </section>

      {/* Contact form */}
      <section id="contacto" className="py-24 md:py-32">
        <section className="container max-w-2xl">
          {!sent ? (
            <>
              <div className="text-center mb-12">
                <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-foreground mb-4">
                  <Lock className="w-3 h-3" /> Conversación confidencial
                </p>
                <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                  Agenda una reunión privada.
                </h2>
                <p className="text-white/60 mt-4 font-light">
                  Cobertura bajo NDA. Respuesta en menos de 24h por un socio director.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-[#0F151D] border border-white/10 rounded-lg p-8 md:p-10 space-y-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">Nombre</Label>
                  <Input
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className={`bg-[#0A0E13] border-white/10 text-white h-11 ${errors['full_name'] ? "border-destructive" : ""}`}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">Fondo / Family Office</Label>
                  <Input
                    value={form.fund}
                    onChange={(e) => setForm({ ...form, fund: e.target.value })}
                    className={`bg-[#0A0E13] border-white/10 text-white h-11 ${errors['fund'] ? "border-destructive" : ""}`}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">Objetivo</Label>
                  <Select value={form.objective} onValueChange={(v) => setForm({ ...form, objective: v })}>
                    <SelectTrigger className={`bg-[#0A0E13] border-white/10 text-white h-11 ${errors['objective'] ? "border-destructive" : ""}`}>
                      <SelectValue placeholder="Selecciona el objetivo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="due_diligence">Operational Due Diligence</SelectItem>
                      <SelectItem value="turnaround">Turnaround Management</SelectItem>
                      <SelectItem value="growth">Crecimiento / Buy & Build</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">Email corporativo</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={`bg-[#0A0E13] border-white/10 text-white h-11 ${errors['email'] ? "border-destructive" : ""}`}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 font-semibold uppercase tracking-wider text-xs"
                >
                  {sending ? "Enviando..." : "Agendar reunión confidencial"}
                </Button>

                <p className="flex items-center justify-center gap-2 text-xs text-white/50 pt-2 text-center">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  🔒 Tus datos están protegidos. Un socio director contactará contigo en menos de 24h.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-20 bg-[#0F151D] border border-white/10 rounded-lg">
              <CheckCircle2 className="w-14 h-14 text-foreground mx-auto mb-6" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-semibold mb-3">Solicitud confidencial recibida.</h2>
              <p className="text-white/60 max-w-md mx-auto">
                Un socio director te contactará en menos de 24h por el canal indicado.
              </p>
            </div>
          )}
        </section>
      </section>

      {/* Footer mini */}
      <div className="border-t border-white/5">
        <section className="container max-w-6xl py-8 flex items-center justify-between text-xs text-white/40">
          <span>© Retail Lemon · Investor Relations</span>
          <Link to="/" className="hover:text-white transition-colors">Volver a la web principal →</Link>
        </section>
      </div>
    </div>
  );
}
